import { nullDate } from "shared/utils/common";
import yup from "../../../shared/validation/yupExtensions";
import { ListModel, ListModelWithDateAndBoolValue, ListModelWithType, MainResponse, Message, TranslationList } from "shared/interfaces/core-interfaces";

export interface ApplicantTrackingSliceModel {
    statusCode: string,
    isLoading: boolean,
    message: string,
    hiringRequests: HiringRequestsInSlice,
    applicants: ApplicantsInSlice
    emailData: EmailsInSlice;
}

export interface HiringRequestsInSlice {
    isModalLoading: boolean,
    hiringRequestByPositionList: Record<string, HiringRequest[]>
    hiringRequests: HiringRequest[];
    inactiveHiringRequests: HiringRequest[];
    hiringRequest: HiringRequest | null
    pdfTabData: PdfTabData,
    createData: HiringRequestCreateData
}

export interface HiringRequest {
    id?: string | null;
    name: string;
    positionId: string;
    positionName: string;
    numberOfPeople: number;
    departmentId: string;
    formalEducationReq?: string | null;
    formalEducationOpt?: string | null;
    relevantWorkingExpReq?: string | null;
    relevantWorkingExpOpt?: string | null;
    skillsReq?: ListModel<string>[] | null;
    skillsOpt?: ListModel<string>[] | null;
    languagesReq?: ListModel<string>[] | null;
    languagesOpt?: ListModel<string>[] | null;
    licencesReq?: string | null;
    licencesOpt?: string | null;
    timeToHire: string;
    contractDuration?: string | null;
    startingSalaryRange?: string | null;
    hiringManagerId: string;
    tenantId: string;
    tenantLocationId: string;
    date: Date;
    createdAt?: Date | null;
    isDefault: boolean;
    isApproved: boolean;
}

export interface HiringRequestByPosition {
    id?: string | null;
    positionId: string;
    hiringManagerId: string;
    departmentId: string;
    tenantId: string;
    tenantLocationId: string;
}

export interface HiringRequestCreateData {
    positions: ListModel<string>[]
    employees: ListModel<string>[]
    departments: ListModel<string>[]
    languages: ListModel<string>[]
    tenants: ListModel<string>[]
    tenantLocations: Record<string, ListModel<string>[]>;
}

export interface PdfTabData {
    additionalPdfData: AdditionalPdfDataModel,
    documentFiles: DocumentPdfModel[],
}

export interface DocumentPdfModel {
    id: string,
    fileName: string,
    version: number,
    createdBy: string,
    createdAt: Date,
}

export interface AdditionalPdfDataModel {
    language: string,
    additionalLanguage?: string | null
}

export interface SourceInfo {
    isSourceDropdownLoading: boolean,
    newSourceId: string | null;
}

export interface ApplicantsInSlice {
    isModalLoading: boolean,
    newApplicantList: NewApplicantResponse[]
    newApplicant: NewApplicantResponse | null
    applicantByHiringRequestList: Record<string, ApplicantResponse[]>
    applicants: ApplicantResponse[]
    applicant: Applicant | null
    education: Education | null
    experience: Experience | null
    skills: Skills | null
    feedbacks: Record<string, FeedbackResponse | null> | null
    attachments: Attachments | null
    applicationHistory: ApplicationHistoryItem[] | null
    createData: ApplicantCreateData
    sourceInfo: SourceInfo
}

//#region NewApplicant

export interface GetEmailsRequest {
    tenantId: string;
    nextPageLink: string;
}

export interface UpdateEmailRequest {
    emailId: string;
    isCreated: boolean;
}

export interface EmailsResponse {
    messages: MessageResponse[];
    nextPageLink: string;
}

export interface MessageResponse {
    message: Message;
    isCreated: boolean;
}

export interface EmailsInSlice extends EmailsResponse {
    isLoading: boolean;
    isLoadingMore: boolean;
    tenantId: string;
}

export interface NewApplicantRequest {
    id?: string | null;
    firstName: string;
    lastName: string;
    email: string;
    sourceId: string;
}

export interface NewApplicantResponse extends NewApplicantRequest {
    date: Date;
}

export interface NewApplicantToAssign {
    newApplicantId: string;
    hiringRequestId: string;
}

//#endregion

export interface Applicant {
    id?: string | null;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string | null,
    secondaryEmail?: string | null;
    statusId?: number | null;
    phaseId?: number | null;
    genderId: number;
    dateOfBirth?: Date | null;
    title?: string | null;
    titleAfter?: string | null;
    countryId?: number | null;
    city?: string | null;
    address?: string | null;
    completedMilitaryService: boolean;
    religionId?: number | null;
    martialStatus?: string | null;
    citizenship?: ListModel<string>[] | null;
    hasEducation?: boolean | null;
    hasExperience?: boolean | null;
    hasSkills?: boolean | null;
    hasFeedback?: boolean | null;
    hasAttachments?: boolean | null;
    hasApplicationHistory?: boolean | null;
}

export interface ApplicantResponse extends Applicant {
    hiringRequestId: string;
    applicationDate: Date;
    sourceId: string;
    feedbackGrade: number;
    tenantLocationId: string;
    userId: string | null
}

export interface ApplicantForEmployeeResponse extends ApplicantResponse {
    departmentId: string;
    positionId: string;
}

export interface ApplicantForEmployeeRequest {
    applicantId: string;
    hiringRequestId: string;
}

export interface ApplicantToReassign {
    applicantId: string;
    oldHiringRequestId: string;
    newHiringRequestId: string;
}

export interface ApplicantStatusChange {
    applicantId: string;
    hiringRequestId: string;
    statusId: number;
}

export interface ApplicantPhaseChange {
    applicantId: string;
    hiringRequestId: string;
    phaseId: number;
}

export interface DeleteApplicantRequest {
    applicantId: string;
    hiringRequestId: string;
}

export interface EducationLevel {
    id?: string | null;
    educationType: string;
    from?: string | null;
    to?: string | null;
    institute: string;
    specialization?: string | null;
    isFinished: boolean;
    isUpdated: boolean;
}

export interface Language {
    id?: string | null;
    language: string;
    degreeId?: number | null;
    isNative: boolean;
    isUpdated: boolean;
}

export interface Education {
    applicantId?: string | null;
    educationLevels?: EducationLevel[];
    languages?: Language[];
    drivingLicences?: ListModel<string>[];
    hobbies?: string;
    skills?: string;
}

export interface ExperienceList {
    id?: string | null;
    from?: Date | null;
    to?: Date | null;
    company: string;
    location?: string | null;
    country?: number | null;
    position?: string | null;
    description?: string | null;
    contactPerson?: string | null;
    isProjectExp: boolean;
    system?: string | null;
    isUpdated: boolean;
}

export interface Experience {
    applicantId?: string | null;
    experienceRecords?: ExperienceList[];
    projectExperienceRecords?: ExperienceList[];
}

export interface SkillWithGrade {
    id?: string | null;
    title: string;
    value: string;
    type?: number | null;
}

export interface Skills {
    applicantId?: string | null;
    skillsGrades: SkillWithGrade[];
}

export interface FeedbackTab {
    applicantId?: string | null;
}

export interface FeedbackRequest {
    applicantId?: string | null;
    hiringRequestId?: string | null;
    reviewersIds: ListModel<string>[] | [];
    deadline: Date;
}

export interface GeneralFeedbackRequest {
    applicantId?: string | null;
    hiringRequestId?: string | null;
    generalFeedback: string;
}

export interface PersonalFeedbackRequest {
    applicantId?: string | null;
    hiringRequestId?: string | null;
    feedbackComment: string;
    grade: number;
}

export interface FeedbackResponse extends FeedbackRequest {
    generealFeedback?: string | null;
    feedbackGivenOn?: Date | null;
    feedbackComment?: string | null;
    grade?: number | null;
    reviewerComments: ReviewerComment[]
}

export interface ReviewerComment {
    id: string;
    employeeId: string;
    feedbackComment?: string;
    grade?: number;
    lastUpdated?: Date | null;
}

export interface Attachments {
    applicantId?: string | null;
    files?: ApplicantAttachment[] | null;
}

export interface ApplicantAttachmentRequest {
    id?: string | null;
    fileName: string;
    description?: string | null;
    fileBase64?: string;
    fileType: number;
    createdAt?: Date | null;
    createdBy?: string | null;
    isEditing: boolean;
}

export interface ApplicantAttachment {
    id?: string | null;
    fileName: string;
    description?: string | null;
    fileBase64?: string | null;
    fileType: number;
    createdAt?: Date | null;
    createdBy?: string | null;
}

export interface AttachmentsResponse {
    message: string;
    files: ApplicantAttachment[];
}

export interface DownloadAttachmentRequest {
    applicantId: string;
    fileTypeId?: number | null;
    fileId?: string | null;
}

export interface ApplicationHistory {
    applicantId?: string | null;
    applicantHistoryDTOs: ApplicationHistoryItem[];
}

export interface ApplicationHistoryRequest {
    applicantId?: string | null;
    applicantHistoryDTOs: ApplicationHistoryItemRequest[];
}

export interface ApplicationHistoryItem {
    hiringRequestId: string;
    sourceId: string;
    statusId: number;
    date: Date;
    phaseIds: Phase[];
}

export interface ApplicationHistoryItemRequest {
    hiringRequestId: string;
    sourceId: string;
    statusId: number;
    date: Date;
    phaseIds: number[];
}

export interface Phase {
    phaseId: string;
    date: Date;
    hiringRequestApplicantPhaseId: string;
}

export interface HiringCampaignListResponse {
    value: string;
    label: string;
    isActive: boolean;
    boolValue: boolean;
    date: Date;
    position: string;
    tenant: string;
    tenantLocation: string;
}

export interface ApplicantCreateData {
    applicantStatuses: ListModelWithType<number>[]
    applicantPhases: ListModelWithType<number>[]
    applicantEmails: ListModel<string>[]
    genders: ListModel<number>[]
    applicantSources: TranslationList[] | [],
    countries: ListModel<number>[]
    citizenships: ListModel<string>[]
    religions: ListModel<number>[]
    martialStatuses: ListModel<string>[]
    hiringCampaigns: HiringCampaignListResponse[]
    languages: ListModel<string>[]
    languageDegrees: ListModel<number>[]
    drivingLicences: ListModel<string>[]
    applicantFileTypes: ListModel<number>[]
    additionalInfoTemplates: ListModel<string>[]
    employees: ListModel<string>[]
    tenantLocations: ListModel<string>[]
}

//#region initial states

export const initialHiringRequestValue = {
    id: null,
    name: "",
    positionId: "",
    numberOfPeople: undefined,
    departmentId: "",
    timeToHire: "",
    hiringManagerId: "",
    date: nullDate,
    positionName: "",
    tenantLocationId: "",
    isDefault: false,
    isApproved: false
}

export const initialNewApplicantValue: NewApplicantRequest = {
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    sourceId: "",
}

export const initialApplicantValue: Applicant = {
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    secondaryEmail: null,
    dateOfBirth: null,
    completedMilitaryService: false,
    citizenship: null,
    hasEducation: false,
    hasExperience: false,
    hasSkills: false,
    hasFeedback: false,
    hasAttachments: false,
    hasApplicationHistory: false,
    genderId: 0
}

export const initialEducationLevelValue: EducationLevel = {
    educationType: "",
    institute: "",
    isFinished: false,
    isUpdated: false
}

export const initialLanguageValue: Language = {
    language: "",
    degreeId: null,
    isNative: false,
    isUpdated: false
}

export const initialEducationValue: Education = {
    applicantId: null
}

export const initialExperienceListValue: ExperienceList = {
    company: "",
    location: "",
    position: "",
    description: "",
    contactPerson: "",
    isProjectExp: false,
    isUpdated: false,
    country: 0,
    from: null,
    to: null
}

export const initialExperienceValue: Experience = {
    applicantId: null
}

export const initialSkillWithGradeValue: SkillWithGrade = {
    title: "",
    value: ""
}

export const initialSkillsValue: Skills = {
    skillsGrades: []
}

export const initialFeedbackValue: FeedbackRequest = {
    applicantId: "",
    reviewersIds: [],
    deadline: nullDate,
    hiringRequestId: ""
}

export const initialApplicantAttachmentValue: ApplicantAttachmentRequest = {
    fileName: "",
    fileBase64: "",
    fileType: 0,
    createdAt: undefined,
    isEditing: false
}

export const initialAttachmentsValue: Attachments = {
    applicantId: null
}

export const initialApplicationHistoryValue: ApplicationHistory = {
    applicantHistoryDTOs: []
}

export const initialApplicationHistoryItemValue: ApplicationHistoryItem = {
    hiringRequestId: "",
    sourceId: "",
    statusId: 0,
    date: nullDate,
    phaseIds: []
}

export const initialHiringRequestInSliceValue: HiringRequestsInSlice = {
    isModalLoading: false,
    hiringRequestByPositionList: {},
    hiringRequest: null,
    pdfTabData: {
        additionalPdfData: {
            language: "",
            additionalLanguage: ""
        },
        documentFiles: []
    },
    createData: {
        positions: [],
        employees: [],
        departments: [],
        languages: [],
        tenants: [],
        tenantLocations: {},
    },
    hiringRequests: [],
    inactiveHiringRequests: []
}

export const initialApplicantsInSliceValue: ApplicantsInSlice = {
    isModalLoading: false,
    newApplicantList: [],
    applicantByHiringRequestList: {},
    applicant: null,
    createData: {
        applicantStatuses: [],
        genders: [],
        countries: [],
        citizenships: [],
        religions: [],
        martialStatuses: [],
        applicantSources: [],
        hiringCampaigns: [],
        languages: [],
        languageDegrees: [],
        drivingLicences: [],
        applicantFileTypes: [],
        additionalInfoTemplates: [],
        employees: [],
        tenantLocations: [],
        applicantPhases: [],
        applicantEmails: []
    },
    education: null,
    experience: null,
    skills: null,
    feedbacks: null,
    attachments: null,
    applicationHistory: null,
    newApplicant: null,
    applicants: [],
    sourceInfo: {
        isSourceDropdownLoading: false,
        newSourceId: null
    }
}

export const initialSliceValue: ApplicantTrackingSliceModel = {
    statusCode: "",
    isLoading: false,
    message: "",
    hiringRequests: initialHiringRequestInSliceValue,
    applicants: initialApplicantsInSliceValue,
    emailData: {
        messages: [],
        nextPageLink: "",
        isLoading: false,
        isLoadingMore: false,
        tenantId: ""
    }
}

//#endregion

export const HiringRequestModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    name: yup.string().noLeadingSpace().required("Name is required."),
    positionId: yup.string().required("Position is required."),
    positionName: yup.string().noLeadingSpace().required("Position name is required."),
    numberOfPeople: yup.number().transform((value) => Number.isNaN(value) ? null : value).required("Number of people is required.").moreThan(0, "At least one person is required."),
    departmentId: yup.string().required("Department is required."),
    formalEducationReq: yup.string().optional().nullable(),
    formalEducationOpt: yup.string().optional().nullable(),
    relevantWorkingExpReq: yup.string().optional().nullable(),
    relevantWorkingExpOpt: yup.string().optional().nullable(),
    skillsReq: yup.array().optional().nullable(),
    skillsOpt: yup.array().optional().nullable(),
    languagesReq: yup.array<ListModel<string>[]>().optional().nullable(),
    languagesOpt: yup.array<ListModel<string>[]>().optional().nullable(),
    licencesReq: yup.string().optional().nullable(),
    licencesOpt: yup.string().optional().nullable(),
    timeToHire: yup.string().noLeadingSpace().required("Hiring time frame is required."),
    contractDuration: yup.string().optional().nullable(),
    startingSalaryRange: yup.string().optional().nullable(),
    hiringManagerId: yup.string().required("Hiring manager is required."),
    tenantId: yup.string().required("Tenant is required."),
    tenantLocationId: yup.string().required("Tenant location is required."),
    date: yup.date().typeError("Please type valid date.").required("Possible starting date is required."),
    createdAt: yup.date().nullable(),
    isDefault: yup.boolean().required(),
    isApproved: yup.boolean().required(),
});

export const HiringRequestByPositionModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    positionId: yup.string().required("Position is required."),
    hiringManagerId: yup.string().required("Hiring manager is required."),
    departmentId: yup.string().required("Department is required."),
    tenantId: yup.string().required("Tenant is required."),
    tenantLocationId: yup.string().required("Tenant location is required."),
});

export const AdditionalPdfDataModelScheme = yup.object().shape({
    language: yup.string().required('Language is required field.'),
    additionalLanguage: yup.string().nullable()
});

export const NewApplicantRequestModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    firstName: yup.string().noLeadingSpace().required("First name is required."),
    lastName: yup.string().noLeadingSpace().required("Last name is required."),
    email: yup.string().noLeadingSpace().validEmail("Invalid email format.").required("Email is required."),
    sourceId: yup.string().defined().required("Source is required."),
});

export const ApplicantModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    firstName: yup.string().noLeadingSpace().required("First name is required."),
    lastName: yup.string().noLeadingSpace().required("Last name is required."),
    email: yup.string().noLeadingSpace().validEmail("Invalid email format.").required("Email is required."),
    phoneNumber: yup.string().noLeadingSpace().validInternationalPhoneNumber().validPhoneNumber().nullable(),
    secondaryEmail: yup.string().noLeadingSpace().validEmail("Invalid email format.").optional().nullable(),
    statusId: yup.number().optional().nullable(),
    genderId: yup.number().min(1, "Gender is required.").required("Gender is required."),
    dateOfBirth: yup.date().optional().nullable().typeError("Please type valid date."),
    title: yup.string().optional().nullable(),
    titleAfter: yup.string().optional().nullable(),
    countryId: yup.number().optional().nullable(),
    city: yup.string().optional().nullable(),
    address: yup.string().optional().nullable(),
    completedMilitaryService: yup.boolean().required(),
    religionId: yup.number().optional().nullable(),
    martialStatus: yup.string().optional().nullable(),
    citizenship: yup.array<ListModel<string>[]>().optional().nullable(),
    hasEducation: yup.boolean().optional().nullable(),
    hasExperience: yup.boolean().optional().nullable(),
    hasSkills: yup.boolean().optional().nullable(),
    hasFeedback: yup.boolean().optional().nullable(),
    hasAttachments: yup.boolean().optional().nullable(),
    hasApplicationHistory: yup.boolean().optional().nullable(),
});

export const EducationLevelModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    educationType: yup.string().required("Type is required."),
    from: yup.string()
        .test(
            "from-less-than-or-equal-to-to",
            "Invalid years.",
            function (value) {
                const { to } = this.parent;
                if (to && value) {
                    return parseInt(value) <= parseInt(to);
                }
                return true;
            }
        ).optional().nullable(),
    to: yup.string()
        .test(
            "to-greater-than-from",
            "Invalid years.",
            function (value) {
                const { from } = this.parent;
                if (from && value) {
                    return parseInt(value) >= parseInt(from);
                }
                return true;
            }
        ).optional().nullable(),
    institute: yup.string().required("Institute is required."),
    specialization: yup.string().optional().nullable(),
    isFinished: yup.boolean().required("Diploma field is required."),
    isUpdated: yup.boolean().required(),
});

export const LanguageModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    language: yup.string().required("Language is required."),
    degreeId: yup.number().nullable().optional(),
    isNative: yup.boolean().required("Native language field is required."),
    isUpdated: yup.boolean().required(),
});

export const EducationModelScheme = yup.object().shape({
    applicantId: yup.string().optional().nullable(),
    educationLevels: yup.array().of(EducationLevelModelScheme).optional(),
    languages: yup.array().of(LanguageModelScheme).optional(),
    drivingLicences: yup.array().optional(),
    hobbies: yup.string(),
    skills: yup.string(),
});

// A date left half-typed is reported on its own field only, not compared against the other one.
const isRealDate = (value: unknown): value is Date => value instanceof Date && !isNaN(value.getTime());

export const ExperienceListModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    from: yup.date().typeError("Please type valid date.")
        .test(
            "from-less-than-or-equal-to-to",
            "From date cannot be later than to date.",
            function (value) {
                const { to } = this.parent;
                if (isRealDate(to) && isRealDate(value)) {
                    return value <= to;
                }
                return true;
            }
        ).optional().nullable(),
    to: yup.date().typeError("Please type valid date.")
        .test(
            "to-greater-than-from",
            'To date cannot be earlier than from date.',
            function (value) {
                const { from } = this.parent;
                if (isRealDate(from) && isRealDate(value)) {
                    return value >= from;
                }
                return true;
            }
        ).optional().nullable(),
    company: yup.string().required("Company is required."),
    location: yup.string().optional().nullable(),
    country: yup.number().optional().nullable(),
    position: yup.string().optional().nullable(),
    description: yup.string().optional().nullable(),
    contactPerson: yup.string().optional().nullable(),
    isProjectExp: yup.boolean().required("Project experience field is required."),
    system: yup.string().optional().nullable(),
    isUpdated: yup.boolean().required("Update status is required."),
});

export const ExperienceModelScheme = yup.object().shape({
    applicantId: yup.string().nullable(),
    experienceRecords: yup.array().of(ExperienceListModelScheme).optional(),
    projectExperienceRecords: yup.array().of(ExperienceListModelScheme).optional(),
});

export const SkillWithGradeModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    title: yup.string().required("Skill is required."),
    value: yup.string().required("Rating is required."),
    type: yup.number().optional().nullable(),
});

export const SkillsModelScheme = yup.object().shape({
    applicantId: yup.string().nullable(),
    skillsGrades: yup.array().of(SkillWithGradeModelScheme).required()
});

export const FeedbackModelScheme = yup.object().shape({
    applicantId: yup.string().nullable(),
    hiringRequestId: yup.string().nullable(),
    reviewersIds: yup.array().min(1, 'At least one reviewer is required.').required('Reviewers list is required.'),
    deadline: yup.date().required('Deadline is required.').min(new Date(), 'Deadline must be in the future.'),
});

export const GeneralFeedbackModelScheme = yup.object().shape({
    applicantId: yup.string().nullable(),
    hiringRequestId: yup.string().nullable(),
    generalFeedback: yup.string().required('Feedback is required.'),
});

export const PersonalFeedbackModelScheme = yup.object().shape({
    applicantId: yup.string().nullable(),
    hiringRequestId: yup.string().nullable(),
    feedbackComment: yup.string().required('Feedback is required.'),
    grade: yup.number().required("Grade is required.")
});

export const ApplicantAttachmentModelScheme = yup.object().shape({
    id: yup.string().nullable(),
    fileName: yup.string().required(),
    description: yup.string().nullable(),
    fileBase64: yup.string().when('isEdit', {
        is: false,
        then: schema => schema.required('File is required'),
        otherwise: schema => schema.notRequired(),
    }),
    fileType: yup.number().required("File type is required."),
    createdAt: yup.date().nullable(),
    createdBy: yup.string().nullable(),
    isEditing: yup.boolean().required(),
});

export const AttachmentsModelScheme = yup.object().shape({
    applicantId: yup.string().nullable(),
    files: yup.array().of(ApplicantAttachmentModelScheme).nullable(),
});

export interface Phase {
    phaseId: string;
    date: Date;
    hiringRequestApplicantPhaseId: string;
}

export const PhaseModelScheme = yup.object().shape({
    phaseId: yup.string().required(),
    date: yup.date().required(),
    hiringRequestApplicantPhaseId: yup.string().required()
});

export const ApplicationHistoryItemModelScheme = yup.object().shape({
    hiringRequestId: yup.string().required('Hiring campaign is required.'),
    sourceId: yup.string().required('Source is required.'),
    statusId: yup.number().min(1, 'Status is required.').required('Status is required.'),
    date: yup.date().required('Date is required.'),
    phaseIds: yup.array().of(PhaseModelScheme).required()
});

export const ApplicationHistoryModelScheme = yup.object().shape({
    applicantId: yup.string().nullable(),
    applicantHistoryDTOs: yup.array().of(ApplicationHistoryItemModelScheme).required(),
});